import { useMemo, useState } from 'react';
import { TabContext, TabPanel } from '@mui/lab';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router';
import { PageContainer } from '@/components/shared/page-container';
import { PageHeader } from '@/components/shared/page-header';
import { useAcademicSituationsIndex } from '@/data/learning/academic-situation';
import { useSetTitle } from '@/hooks/use-set-title';
import {
  SituationSort,
  SituationTab,
  situationTabOrder,
  situationTabStatuses,
} from '@/packages/academic-hub/constants';
import { EmptyHubState } from '@/packages/academic-hub/components/empty-hub-state';
import { NewCourseDialog } from '@/packages/academic-hub/components/new-course-dialog';
import { SituationFilters } from '@/packages/academic-hub/components/situation-filters';
import { SituationList } from '@/packages/academic-hub/components/situation-list';
import { SituationListSkeleton } from '@/packages/academic-hub/components/situation-list/skeleton';
import { SituationTabs } from '@/packages/academic-hub/components/situation-tabs';
import type { CourseTypes } from '@/models/catalog/course';
import type { AcademicSituation } from '@/models/learning/academic-situation';

const applyCategories = (
  situations: AcademicSituation[],
  categories: CourseTypes[]
) =>
  categories.length === 0
    ? situations
    : situations.filter((situation) =>
        categories.includes(situation.embedded_course.course_type)
      );

const sortableTime = (situation: AcademicSituation) =>
  new Date(situation.started_at ?? situation.created_at).getTime();

const applySort = (situations: AcademicSituation[], sort: SituationSort) =>
  [...situations].sort((first, second) => {
    const firstTime = sortableTime(first);
    const secondTime = sortableTime(second);

    return sort === SituationSort.Newest
      ? secondTime - firstTime
      : firstTime - secondTime;
  });

const groupByTab = (situations: AcademicSituation[]) =>
  situationTabOrder.reduce(
    (acc, tab) => {
      acc[tab] = situations.filter((situation) =>
        situationTabStatuses[tab].includes(situation.current_status)
      );
      return acc;
    },
    {} as Record<SituationTab, AcademicSituation[]>
  );

export const AcademicHub = () => {
  useSetTitle('Hub Acadêmico');
  const navigate = useNavigate();
  const [tab, setTab] = useState(SituationTab.Pending);
  const [categories, setCategories] = useState<CourseTypes[]>([]);
  const [sort, setSort] = useState(SituationSort.Newest);
  const [isNewCourseOpen, setIsNewCourseOpen] = useState(false);
  const { academicSituations, isLoading } = useAcademicSituationsIndex();

  const goToCourses = () => navigate('/cursos');

  const openNewCourse = () => setIsNewCourseOpen(true);

  const groupedSituations = useMemo(
    () =>
      groupByTab(applySort(applyCategories(academicSituations, categories), sort)),
    [academicSituations, categories, sort]
  );

  const counts = useMemo(
    () =>
      situationTabOrder.reduce(
        (acc, situationTab) => {
          acc[situationTab] = groupedSituations[situationTab].length;
          return acc;
        },
        {} as Record<SituationTab, number>
      ),
    [groupedSituations]
  );

  return (
    <PageContainer>
      <PageHeader
        title="Hub Acadêmico"
        description="Todos os seus cursos, em um só lugar. Acompanhe o andamento de todos os seus estudos por aqui."
        buttonText="Adicionar curso"
        buttonOnClick={openNewCourse}
      />
      {isLoading ? (
        <SituationListSkeleton />
      ) : academicSituations.length === 0 ? (
        <EmptyHubState
          onSearchCourses={goToCourses}
          onAddExternalCourse={openNewCourse}
        />
      ) : (
        <TabContext value={tab}>
          <Box sx={{ position: 'relative', mt: 2 }}>
            <SituationTabs value={tab} counts={counts} onChange={setTab} />
            <SituationFilters
              categories={categories}
              sort={sort}
              onCategoriesChange={setCategories}
              onSortChange={setSort}
            />
          </Box>
          {situationTabOrder.map((situationTab) => (
            <TabPanel
              key={situationTab}
              value={situationTab}
              sx={{ px: 0, pt: 3, pb: 0 }}
            >
              <SituationList
                situations={groupedSituations[situationTab]}
                onSearchCourses={goToCourses}
                onAddExternalCourse={openNewCourse}
              />
            </TabPanel>
          ))}
        </TabContext>
      )}
      {isNewCourseOpen && (
        <NewCourseDialog onClose={() => setIsNewCourseOpen(false)} />
      )}
    </PageContainer>
  );
};
